/**
 * Wix Media CDN safety helper. Customer-uploaded images can exceed
 * Wix's render budget and HTTP 400 from the CDN. Apply this at every
 * image render and fetch site so the URL is always within budget.
 *
 * Limits (from `docs/research/legacy-extra-docs-synthesis.md`):
 *   - total `width × height ≤ 33,177,600` px
 *   - per-axis ≤ 5000 (fit / fill / crop transforms)
 *   - WebP per-axis ≤ 16383
 *
 * The legacy app surfaced this as `getWixMediaSafeUrl()` applied at
 * 12 render/fetch sites. We retrofit by routing every image src
 * through this helper from day one — easier than retrofitting later.
 */

export const WIX_MEDIA_MAX_PIXELS = 33_177_600;
export const WIX_MEDIA_MAX_DIMENSION = 5_000;

/**
 * Wix media URLs follow the shape:
 *   https://static.wixstatic.com/media/{id}/v1/{transform}/w_{w},h_{h},q_{q}/file.{ext}
 *   https://static.wixstatic.com/media/{id}/v1/fill/w_{w},h_{h},al_c,q_{q}/file.{ext}
 *
 * `{transform}` is one of `fit`, `fill`, `crop` (we don't differentiate;
 * any of them gets the same axis cap).
 */
const WIX_MEDIA_URL = /^(https?:\/\/[^/]*wixstatic\.com\/media\/[^/]+\/v1\/[^/]+\/)([^/]+)(\/.+)$/;

interface ParsedTransform {
  width: number;
  height: number;
  quality?: number;
  /** Anything else in the transform segment (`al_c`, `usm_…`, etc.). */
  passthrough: string[];
}

function parseTransform(seg: string): ParsedTransform | null {
  const parts = seg.split(',');
  let width = 0;
  let height = 0;
  let quality: number | undefined;
  const passthrough: string[] = [];
  for (const p of parts) {
    if (p.startsWith('w_')) {
      const n = Number(p.slice(2));
      if (Number.isFinite(n)) width = n;
    } else if (p.startsWith('h_')) {
      const n = Number(p.slice(2));
      if (Number.isFinite(n)) height = n;
    } else if (p.startsWith('q_')) {
      const n = Number(p.slice(2));
      if (Number.isFinite(n)) quality = n;
    } else {
      passthrough.push(p);
    }
  }
  if (width === 0 && height === 0) return null;
  return { width, height, quality, passthrough };
}

function buildTransform(t: ParsedTransform): string {
  const parts = [`w_${t.width}`, `h_${t.height}`];
  if (t.quality != null) parts.push(`q_${t.quality}`);
  parts.push(...t.passthrough);
  return parts.join(',');
}

/**
 * Return a Wix Media URL guaranteed to be within the CDN's render budget.
 * For non-Wix URLs (or unparseable shapes) the input is returned unchanged.
 *
 * Strategy:
 *   1. If width × height exceeds `WIX_MEDIA_MAX_PIXELS`, scale both
 *      sqrt-proportionally so the product fits.
 *   2. If either axis still exceeds `WIX_MEDIA_MAX_DIMENSION`, cap both
 *      axes at 5000 and switch the transform to `fit` (preserves aspect).
 *   3. If neither limit is breached, return the input unchanged.
 */
export function getWixMediaSafeUrl(url: string | null | undefined): string {
  if (!url) return '';
  const match = WIX_MEDIA_URL.exec(url);
  if (!match) return url;

  const [, head, transformSeg, tail] = match;
  if (!head || !transformSeg || !tail) return url;
  const parsed = parseTransform(transformSeg);
  if (!parsed) return url;

  let { width, height } = parsed;
  let mutated = false;

  // Step 1: total-pixel cap (scale proportionally).
  const totalPixels = width * height;
  if (totalPixels > WIX_MEDIA_MAX_PIXELS) {
    const scale = Math.sqrt(WIX_MEDIA_MAX_PIXELS / totalPixels);
    width = Math.floor(width * scale);
    height = Math.floor(height * scale);
    mutated = true;
  }

  // Step 2: per-axis cap. If either axis still > MAX_DIMENSION, fall back
  // to fit/5000x5000/q_90 — preserves aspect via the CDN's fit logic.
  if (width > WIX_MEDIA_MAX_DIMENSION || height > WIX_MEDIA_MAX_DIMENSION) {
    const headFit = head.replace(/\/v1\/[^/]+\/$/, '/v1/fit/');
    return `${headFit}w_${WIX_MEDIA_MAX_DIMENSION},h_${WIX_MEDIA_MAX_DIMENSION},q_90${tail}`;
  }

  if (!mutated) return url;

  return `${head}${buildTransform({
    width,
    height,
    quality: parsed.quality,
    passthrough: parsed.passthrough,
  })}${tail}`;
}
